import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api';
import { Film, RefreshCw, Trash2, Loader2, PlayCircle, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  none:   { label: 'Kein Video',    className: 'bg-gray-700 text-gray-200' },
  queued: { label: 'In Erstellung', className: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40' },
  ready:  { label: 'Bereit',        className: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
  error:  { label: 'Fehler',        className: 'bg-red-500/20 text-red-300 border border-red-500/40' },
};

export const AdminVideoIntrosPanel = () => {
  const [courses, setCourses] = useState([]);
  const [intros, setIntros] = useState({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // per-module local form state
  const [busy, setBusy] = useState({});
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [coursesRes, introsRes] = await Promise.all([
        apiFetch(`${API}/api/admin/video-intros/core-courses`),
        apiFetch(`${API}/api/admin/video-intros`),
      ]);
      const coursesData = await coursesRes.json();
      const introsData = await introsRes.json();
      setCourses(coursesData.courses || []);
      const map = {};
      (introsData.intros || []).forEach(i => { map[i.module_id] = i; });
      setIntros(map);
    } catch (e) {
      console.error('AdminVideoIntrosPanel load', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 15s while at least one job is queued
  useEffect(() => {
    const hasQueued = Object.values(intros).some(i => i?.status === 'queued');
    if (!hasQueued) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [intros, load]);

  const startGeneration = async (course) => {
    const mid = course.module_id;
    const draft = drafts[mid] || {};
    const body = {
      module_id: mid,
      prompt: (draft.prompt ?? '').trim() || course.default_prompt,
      model: draft.model || 'sora-2',
      size: draft.size || '1280x720',
      duration: parseInt(draft.duration || '8', 10),
    };
    setBusy(prev => ({ ...prev, [mid]: true }));
    try {
      const res = await apiFetch(`${API}/api/admin/video-intros/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Start');
      toast({ title: 'Sora-2 Job gestartet', description: 'Generierung läuft (2-5 Min).', duration: 4000 });
      setIntros(prev => ({ ...prev, [mid]: data }));
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(prev => ({ ...prev, [mid]: false }));
    }
  };

  const deleteIntro = async (mid) => {
    if (!window.confirm('Video wirklich löschen?')) return;
    setBusy(prev => ({ ...prev, [mid]: true }));
    try {
      await apiFetch(`${API}/api/admin/video-intros/${mid}`, { method: 'DELETE' });
      setIntros(prev => {
        const next = { ...prev };
        delete next[mid];
        return next;
      });
      toast({ title: 'Video gelöscht' });
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(prev => ({ ...prev, [mid]: false }));
    }
  };

  const updateDraft = (mid, patch) => {
    setDrafts(prev => ({ ...prev, [mid]: { ...(prev[mid] || {}), ...patch } }));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 p-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Sora-2 Panel…
      </div>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
            <Film className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              Sora-2 Video-Intros
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </CardTitle>
            <CardDescription className="text-gray-400">
              Cinematic 4-12 Sekunden Intros für die 4 Kern-Kurse – erscheinen automatisch vor der ersten Lektion.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {courses.map((c) => {
          const intro = intros[c.module_id];
          const status = intro?.status || 'none';
          const meta = STATUS_META[status] || STATUS_META.none;
          const draft = drafts[c.module_id] || {};
          const isBusy = !!busy[c.module_id] || status === 'queued';

          return (
            <div
              key={c.module_id}
              data-testid={`intro-card-${c.module_id}`}
              className="p-5 rounded-xl border border-yellow-500/20 bg-black/40"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-white font-semibold text-lg">{c.title}</div>
                  <div className="text-xs text-gray-500 font-mono">{c.module_id}</div>
                </div>
                <Badge className={meta.className} data-testid={`intro-status-${c.module_id}`}>
                  {isBusy && status === 'queued' && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                  {meta.label}
                </Badge>
              </div>

              {status === 'ready' && intro?.video_url && (
                <video
                  src={`${API}${intro.video_url}`}
                  controls
                  className="mt-4 w-full max-w-lg rounded-lg border border-emerald-500/20 bg-black"
                  data-testid={`intro-preview-${c.module_id}`}
                />
              )}

              {status === 'error' && intro?.error && (
                <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                  {intro.error}
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Modell</label>
                  <Select
                    value={draft.model || intro?.model || 'sora-2'}
                    onValueChange={(v) => updateDraft(c.module_id, { model: v })}
                  >
                    <SelectTrigger data-testid={`intro-model-${c.module_id}`} className="bg-gray-900 border-gray-700 text-gray-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sora-2">sora-2 (schneller)</SelectItem>
                      <SelectItem value="sora-2-pro">sora-2-pro (Premium)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Größe</label>
                  <Select
                    value={draft.size || intro?.size || '1280x720'}
                    onValueChange={(v) => updateDraft(c.module_id, { size: v })}
                  >
                    <SelectTrigger data-testid={`intro-size-${c.module_id}`} className="bg-gray-900 border-gray-700 text-gray-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1280x720">1280×720 (HD Landscape)</SelectItem>
                      <SelectItem value="1792x1024">1792×1024 (Widescreen)</SelectItem>
                      <SelectItem value="1024x1792">1024×1792 (Portrait)</SelectItem>
                      <SelectItem value="1024x1024">1024×1024 (Square)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Dauer (Sek.)</label>
                  <Select
                    value={String(draft.duration || intro?.duration || 8)}
                    onValueChange={(v) => updateDraft(c.module_id, { duration: v })}
                  >
                    <SelectTrigger data-testid={`intro-duration-${c.module_id}`} className="bg-gray-900 border-gray-700 text-gray-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 Sekunden</SelectItem>
                      <SelectItem value="8">8 Sekunden</SelectItem>
                      <SelectItem value="12">12 Sekunden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-xs text-gray-400 mb-1 block">Prompt (leer = Standard-Prompt)</label>
                <Textarea
                  data-testid={`intro-prompt-${c.module_id}`}
                  rows={3}
                  placeholder={c.default_prompt}
                  value={draft.prompt ?? intro?.prompt ?? ''}
                  onChange={(e) => updateDraft(c.module_id, { prompt: e.target.value })}
                  className="text-sm bg-gray-900 border-gray-700 text-gray-100"
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => startGeneration(c)}
                  disabled={isBusy}
                  data-testid={`intro-generate-${c.module_id}`}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generierung läuft…
                    </>
                  ) : status === 'ready' ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Neu generieren
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Video generieren
                    </>
                  )}
                </Button>
                {status !== 'none' && (
                  <Button
                    variant="outline"
                    onClick={() => deleteIntro(c.module_id)}
                    disabled={isBusy}
                    data-testid={`intro-delete-${c.module_id}`}
                    className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Löschen
                  </Button>
                )}
                <span className="text-xs text-gray-500">
                  ⏱ Generierung dauert 2–5 Min (sora-2), bis 10 Min (sora-2-pro).
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AdminVideoIntrosPanel;
